/*
 * Purpose: SSOT — primary / secondary / tertiary / ghost action tiers per page section
 * Last modified: 2026-07-01 | Agent
 * Completeness: 100
 */
import type { ButtonProps } from '@/components/ui/button';

/** Visual + semantic tier for a single user action. */
export type ActionTier = 'primary' | 'secondary' | 'tertiary' | 'ghost';

/** Max filled primary CTAs in one action group (toolbar, hero, card footer, dialog). */
export const MAX_PRIMARY_ACTIONS_PER_GROUP = 1;

type ButtonVariant = NonNullable<ButtonProps['variant']>;

const TIER_VARIANT: Record<ActionTier, ButtonVariant> = {
  primary: 'default',
  secondary: 'secondary',
  tertiary: 'outline',
  ghost: 'ghost',
};

const TIER_CLASS: Partial<Record<ActionTier, string>> = {
  primary: 'ui-lift-accent',
  tertiary: 'ui-tier-secondary-btn',
};

export function actionTierToVariant(tier: ActionTier): ButtonVariant {
  return TIER_VARIANT[tier];
}

export function actionTierClassName(tier: ActionTier): string | undefined {
  return TIER_CLASS[tier];
}

export function resolveActionTierProps(tier: ActionTier): {
  variant: ButtonVariant;
  className?: string;
} {
  return {
    variant: actionTierToVariant(tier),
    className: actionTierClassName(tier),
  };
}

/** Dev-only guard — warn when a group renders more than one primary CTA. */
export function assertSinglePrimaryAction(
  tiers: ActionTier[],
  context = 'action group'
): void {
  if (!import.meta.env.DEV) return;
  const primaryCount = tiers.filter((t) => t === 'primary').length;
  if (primaryCount > MAX_PRIMARY_ACTIONS_PER_GROUP) {
    console.warn(
      `[actionHierarchy] ${context} has ${primaryCount} primary actions — keep at most ${MAX_PRIMARY_ACTIONS_PER_GROUP}. Demote extras to secondary or ghost.`
    );
  }
}
